import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { notesApiService } from '../services/notesApi';
import { NoteWarning } from '../types/player';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { FileText, TriangleAlert, Search, AlertCircle, ChevronDown, Shield } from 'lucide-react';
import { ShowAutoModToggle } from '../components/ShowAutoModToggle';
import { countAutoModEntries, filterAutoModEntries, isAutoModeration } from '../utils/automod';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreatorProfile } from '../components/CreatorProfile';
import { cn } from '../utils/cn';
import { usernameCacheService } from '../services/usernameCache';
import Fuse from 'fuse.js';
import { CreateNoteWarningModal } from '../components/CreateNoteWarningModal';
import { EvidenceSection } from '../components/EvidenceSection';
import { LogbookCaseSection } from '../components/logbook/LogbookCaseSection';
import { TicketLinks } from '../components/TicketLinks';
import { useIsMobile } from '../hooks/useIsMobile';
import { memo, useRef } from 'react';
import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

const MemoizedEvidenceSection = memo(EvidenceSection);
const MemoizedLogbookCaseSection = memo(LogbookCaseSection);
const MemoizedTicketLinks = memo(TicketLinks);

const EntryTypeBadge = ({ type }: { type: NoteWarning['type'] }) => (
    <span className={cn('badge', type === 'note' ? 'badge-info' : 'badge-warning')}>
        {type === 'note' ? <FileText className="w-3 h-3" /> : <TriangleAlert className="w-3 h-3" />}
        <span className="capitalize">{type}</span>
    </span>
);

const EntryStatusBadge = ({ status }: { status: NoteWarning['status'] }) => (
    <span className={cn('badge badge-dot capitalize', status === 'active' ? 'badge-success' : 'badge-danger')}>
        {status}
    </span>
);

const NoteWarningCardMobile = memo(({ entry, onToggle, isExpanded = false, children }: { entry: NoteWarning, onToggle: (id: string) => void, isExpanded?: boolean, children?: React.ReactNode }) => {
    return (
        <div className={cn("glass-card p-4", isExpanded && "border-accent-400/40")}>
            <div onClick={() => onToggle(entry.id)} className="cursor-pointer">
                <div className="flex justify-between items-start gap-3 mb-2">
                    <PlayerProfile uuid={entry.uuid} />
                    <EntryTypeBadge type={entry.type} />
                </div>
                <p className="text-sm text-white mb-3" title={entry.content}>{entry.content}</p>
                <dl className="text-xs text-glass-400 space-y-1.5">
                    <div className="flex items-center gap-1.5"><dt className="font-medium text-glass-500">Creator</dt><dd className="min-w-0"><CreatorProfile creatorId={entry.creator_id} inline /></dd></div>
                    <div className="flex items-center gap-1.5"><dt className="font-medium text-glass-500">Created</dt><dd className="tabular-nums">{formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}</dd></div>
                    <div className="flex items-center gap-1.5"><dt className="font-medium text-glass-500">Status</dt><dd><EntryStatusBadge status={entry.status} /></dd></div>
                </dl>
                <div className="flex justify-center mt-2 text-glass-500">
                    <ChevronDown className={cn("w-4 h-4 transition-transform", isExpanded && "rotate-180")} />
                </div>
            </div>
            {isExpanded && children && (
                <div className="border-t border-glass-200/10 pt-4 mt-3 space-y-4">{children}</div>
            )}
        </div>
    );
});

export function NotesWarningsView() {
  useDocumentTitle('Notes & Warnings');
  const { identity } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  const [allEntries, setAllEntries] = useState<NoteWarning[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<NoteWarning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalState, setModalState] = useState<{ type: 'note' | 'warning' | null }>({ type: null });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const [showAutoMod, setShowAutoMod] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const prevSearchQuery = useRef('');
  const observer = useRef<IntersectionObserver | null>(null);
  const highlightRef = useRef<HTMLTableRowElement | HTMLDivElement | null>(null);
  const highlightProcessed = useRef(false);

  const canCreate = useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Notes') || groups.has('SPY_Notes_Editor');
  }, [identity]);
  
  useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Notes_Editor');
  }, [identity]);

  const fetchEntries = useCallback(async (search: boolean = false) => {
    if(search) setIsSearching(true);
    else setLoading(true);
    
    setError(null);
    try {
      const response = await notesApiService.getAllEntries(1, 10000); // Fetch all
      const allFetchedEntries = response.results;

      setAllEntries(allFetchedEntries);
      
      const uuidsToFetch = new Set<string>();
      allFetchedEntries.forEach(entry => {
        uuidsToFetch.add(entry.uuid);
        if (entry.creator_id) {
          uuidsToFetch.add(entry.creator_id);
        }
      });
      usernameCacheService.prefetchUsernames(Array.from(uuidsToFetch));
    } catch (err) {
      setError('Failed to fetch entries.');
    } finally {
      if(search) setIsSearching(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const autoModHiddenCount = useMemo(
    () => countAutoModEntries(allEntries),
    [allEntries],
  );

  const displayEntries = useMemo(
    () => filterAutoModEntries(allEntries, showAutoMod),
    [allEntries, showAutoMod],
  );

  // Handle highlight query parameter (e.g., from Evidence Explorer navigation)
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && !loading && allEntries.length > 0 && !highlightProcessed.current) {
      highlightProcessed.current = true;
      const highlightedEntry = allEntries.find((entry) => entry.id === highlightId);
      if (highlightedEntry && isAutoModeration(highlightedEntry.creator_id)) {
        setShowAutoMod(true);
      }
      setExpandedRow(highlightId);
      // Clear the highlight param from URL
      setSearchParams({}, { replace: true });
      // Scroll to highlighted row after a brief delay to allow DOM rendering
      setTimeout(() => {
        if (highlightRef.current) {
          highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [searchParams, loading, allEntries, setSearchParams]);
  
  const handleLoadMore = () => {
    setVisibleCount(prevCount => prevCount + 50);
  };

  const lastEntryElementRef = useCallback((node: HTMLTableRowElement | HTMLDivElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleCount < displayEntries.length) {
        handleLoadMore();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, visibleCount, displayEntries.length]);

  const handleToggleRow = (id: string) => {
    setExpandedRow(current => (current === id ? null : id));
  };

  const fuse = useMemo(() => {
    return new Fuse(displayEntries, {
      keys: ['uuid', 'content', 'creator_id'],
      includeScore: true,
      threshold: 0.4,
      getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path);
        if (path[0] === 'uuid' || path[0] === 'creator_id') {
          return usernameCacheService.getName(value as string) || value;
        }
        return value;
      }
    });
  }, [displayEntries]);

  useEffect(() => {
    const wasSearching = prevSearchQuery.current.trim() !== '';
    const isSearchingNow = searchQuery.trim() !== '';

    if (wasSearching && !isSearchingNow) {
      // Just left search, reset view
      setFilteredEntries(displayEntries.slice(0, visibleCount));
    } else if (isSearchingNow) {
      const results = fuse.search(searchQuery);
      setFilteredEntries(results.map(result => result.item));
    } else {
      setFilteredEntries(displayEntries.slice(0, visibleCount));
    }

    prevSearchQuery.current = searchQuery;
  }, [searchQuery, displayEntries, fuse, visibleCount]);

  useEffect(() => {
    const timer = setTimeout(() => {
        if (searchQuery.trim() !== '') {
            // No need to fetch all on search anymore as we have them
        }
    }, 500); // Debounce search
    return () => clearTimeout(timer);
  }, [searchQuery]);


  const renderDesktopView = () => (
    <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th className="th">Type</th>
              <th className="th">Player</th>
              <th className="th">Content</th>
              <th className="th">Creator</th>
              <th className="th">Created</th>
              <th className="th">Status</th>
              <th className="th text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map((entry, index) => {
              const isExpanded = expandedRow === entry.id;
              return (
              <React.Fragment key={entry.id}>
              <tr
                ref={(node) => {
                  if (index === filteredEntries.length - 1) lastEntryElementRef(node);
                  if (expandedRow === entry.id && searchParams.get('highlight') === null) {
                    highlightRef.current = node;
                  }
                }}
                className={cn("cursor-pointer", isExpanded && "row-active")}
                onClick={() => handleToggleRow(entry.id)}
              >
                <td className="td">
                  <EntryTypeBadge type={entry.type} />
                </td>
                <td className="td min-w-[200px]"><PlayerProfile uuid={entry.uuid} /></td>
                <td className="td text-white max-w-sm truncate" title={entry.content}>{entry.content}</td>
                <td className="td"><CreatorProfile creatorId={entry.creator_id} /></td>
                <td className="td text-glass-300 tabular-nums whitespace-nowrap">{formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}</td>
                <td className="td">
                    <EntryStatusBadge status={entry.status} />
                </td>
                <td className="td">
                  <div className="flex items-center justify-end">
                    <ChevronDown className={cn("w-4 h-4 text-glass-500 transition-transform", isExpanded && "rotate-180")} />
                  </div>
                </td>
              </tr>
              {isExpanded && (
                <tr className="row-detail">
                  <td className="td" colSpan={7}>
                    <div className="space-y-4 py-1">
                      <MemoizedTicketLinks itemId={entry.id} itemType={entry.type} />
                      <MemoizedEvidenceSection itemId={entry.id} itemType={entry.type} />
                      <MemoizedLogbookCaseSection itemType={entry.type} itemId={entry.id} subjectUuid={entry.uuid} />
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {loading && <div className="text-center p-4 text-sm text-glass-400">Loading…</div>}
      </div>
  );

  const renderMobileView = () => (
      <div className="space-y-3">
          {filteredEntries.map((entry, index) => (
              <div key={entry.id} ref={(node) => {
                if (index === filteredEntries.length - 1) lastEntryElementRef(node);
                if (expandedRow === entry.id) {
                  highlightRef.current = node;
                }
              }}>
                  <NoteWarningCardMobile entry={entry} onToggle={() => handleToggleRow(entry.id)} isExpanded={expandedRow === entry.id}>
                      {expandedRow === entry.id && (
                          <>
                              <MemoizedTicketLinks itemId={entry.id} itemType={entry.type} />
                              <MemoizedEvidenceSection itemId={entry.id} itemType={entry.type} />
                          </>
                      )}
                  </NoteWarningCardMobile>
              </div>
          ))}
        {loading && <div className="text-center p-4 text-sm text-glass-400">Loading…</div>}
      </div>
  );

  const renderLoading = () => (
    <div className="table-wrap p-4 space-y-3" aria-busy="true">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <SkeletonLoader className="h-5 w-16 rounded-full" />
          <SkeletonLoader className="h-8 w-8 rounded-md" />
          <SkeletonLoader className="h-4 w-40" />
          <SkeletonLoader className="h-4 flex-1" />
          <SkeletonLoader className="h-4 w-24" />
          <SkeletonLoader className="h-5 w-14 rounded-full" />
        </div>
      ))}
    </div>
  );

  const hasQuery = searchQuery.trim() !== '';
  const showAutoModEmpty = filteredEntries.length === 0 && autoModHiddenCount > 0 && !showAutoMod;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title="Notes & Warnings"
        description="Manage internal staff notes and player warnings."
        icon={FileText}
        meta={!loading && !error ? (
          <>
            <span className="badge badge-neutral">{allEntries.length.toLocaleString()} total</span>
            {autoModHiddenCount > 0 && !showAutoMod && (
              <span className="badge badge-amber">{autoModHiddenCount.toLocaleString()} AutoMod hidden</span>
            )}
          </>
        ) : undefined}
        actions={canCreate ? (
          <>
            <button onClick={() => setModalState({ type: 'warning' })} className="glass-button text-yellow-300 hover:text-yellow-200">
              <TriangleAlert className="w-4 h-4" />
              Create Warning
            </button>
            <button onClick={() => setModalState({ type: 'note' })} className="btn-primary">
              <FileText className="w-4 h-4" />
              Create Note
            </button>
          </>
        ) : undefined}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              placeholder="Search by player, creator, or content…"
              className="glass-input w-full pl-9"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <ShowAutoModToggle
            checked={showAutoMod}
            onChange={setShowAutoMod}
            hiddenCount={autoModHiddenCount}
          />
          {!loading && !error && (
            <span className="text-xs text-glass-400 tabular-nums md:text-right">
              {hasQuery
                ? `${filteredEntries.length.toLocaleString()} match${filteredEntries.length === 1 ? '' : 'es'}`
                : `Showing ${Math.min(visibleCount, displayEntries.length).toLocaleString()} of ${displayEntries.length.toLocaleString()}`}
            </span>
          )}
        </div>
      </PageHeader>

      {loading && renderLoading()}
      {error && (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load entries"
          description={error}
          action={<button onClick={() => fetchEntries()} className="glass-button">Retry</button>}
        />
      )}

      {modalState.type && (
        <CreateNoteWarningModal
            type={modalState.type}
            onClose={() => setModalState({ type: null })}
            onSuccess={() => {
              fetchEntries();
              setVisibleCount(50);
            }}
        />
      )}

      {isSearching && <div className="text-center p-4 text-sm text-glass-400">Searching…</div>}
      {!loading && !isSearching && !error && showAutoModEmpty && (
        <EmptyState
          icon={Shield}
          title="All entries are from AutoMod"
          description={`All ${autoModHiddenCount} entries are from AutoMod and hidden. Enable Show AutoMod above to view them.`}
          action={<button onClick={() => setShowAutoMod(true)} className="glass-button">Show AutoMod</button>}
        />
      )}
      {!loading && !isSearching && !error && !showAutoModEmpty && filteredEntries.length === 0 && (
        <EmptyState
          icon={hasQuery ? Search : FileText}
          title={hasQuery ? 'No entries match your search' : 'No notes or warnings'}
          description={hasQuery ? 'Try a different player, creator or content.' : 'Staff notes and player warnings will appear here.'}
          action={hasQuery ? <button onClick={() => setSearchQuery('')} className="glass-button-secondary">Clear search</button> : undefined}
        />
      )}
      {!loading && !isSearching && !error && filteredEntries.length > 0 && (isMobile ? renderMobileView() : renderDesktopView())}
    </div>
  );
}
